import React from "react";
import { Link } from "react-router-dom";
import { Wallet, TrendingUp, Coins } from "lucide-react";

const fmt = (n, d = 2) =>
  n?.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

/**
 * "Open positions" table for the Paper Trading page.
 *
 * Renders an empty state with a CTA when the portfolio holds nothing, or a
 * responsive row list (desktop = 5 columns, mobile = 2 columns with avg
 * cost/qty hidden). Each row links into the asset detail page.
 */
export default function PositionsTable({ positions = [] }) {
  if (positions.length === 0) {
    return (
      <section className="mb-10">
        <h2 className="font-heading text-2xl text-[#2A2F2B] mb-4">Open positions</h2>
        <div className="rounded-3xl bg-white border border-[#EAE6E1] p-12 text-center" data-testid="positions-empty">
          <div className="w-14 h-14 rounded-full bg-[#F9F8F6] mx-auto flex items-center justify-center mb-4">
            <Wallet className="w-6 h-6 text-[#6B7068]" strokeWidth={1.25} />
          </div>
          <p className="text-[#6B7068] mb-4">No positions yet. Open an asset and tap "Paper trade" to get started.</p>
          <Link to="/dashboard" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-sm transition-soft">
            Explore markets
          </Link>
        </div>
      </section>
    );
  }
  return (
    <section className="mb-10">
      <h2 className="font-heading text-2xl text-[#2A2F2B] mb-4">Open positions</h2>
      <div className="rounded-2xl bg-white border border-[#EAE6E1] overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_100px_120px_120px_140px] gap-4 px-6 py-3 border-b border-[#EAE6E1] text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6B7068]">
          <div>Asset</div><div className="text-right">Qty</div><div className="text-right">Avg cost</div><div className="text-right">Price</div><div className="text-right">P&amp;L</div>
        </div>
        {positions.map((p) => {
          const plColor = p.unrealized_pl >= 0 ? "#5C8D73" : "#C66B59";
          return (
            <Link
              key={`${p.asset_type}-${p.symbol}`}
              to={`/asset/${p.asset_type}/${p.symbol}`}
              data-testid={`position-${p.symbol}`}
              className="grid md:grid-cols-[1fr_100px_120px_120px_140px] grid-cols-[1fr_auto] gap-4 items-center px-6 py-4 border-b border-[#EAE6E1] last:border-0 hover:bg-[#F9F8F6] transition-soft"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: p.asset_type === "stock" ? "#547A8F15" : "#D18E6315" }}
                >
                  {p.asset_type === "stock" ? (
                    <TrendingUp className="w-4 h-4 text-[#547A8F]" strokeWidth={1.75} />
                  ) : (
                    <Coins className="w-4 h-4 text-[#D18E63]" strokeWidth={1.75} />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-mono-ticker font-semibold text-[#2A2F2B]">{p.symbol}</div>
                  <div className="text-xs text-[#6B7068] truncate">{p.name}</div>
                </div>
              </div>
              <div className="hidden md:block text-right font-mono-ticker text-sm text-[#2A2F2B]">{p.quantity.toFixed(4)}</div>
              <div className="hidden md:block text-right font-mono-ticker text-sm text-[#6B7068]">${fmt(p.avg_cost)}</div>
              <div className="hidden md:block text-right font-mono-ticker text-sm text-[#2A2F2B]">${fmt(p.current_price)}</div>
              <div className="text-right font-mono-ticker text-sm font-semibold" style={{ color: plColor }}>
                {p.unrealized_pl >= 0 ? "+" : ""}${fmt(p.unrealized_pl)}
                <div className="text-xs font-normal">{p.unrealized_pct >= 0 ? "+" : ""}{fmt(p.unrealized_pct)}%</div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
